import React, { useEffect } from 'react'
import {
  Container,
  Button,
  Icon,
  TextBody,
  TextCaption,
  TextTitle,
  ListContainer
} from '@pmi/pmi-iqos-ds-react'
import { htmlTranslation } from '@pmi/pmi-iqos-shared-react'
import {
  CartCheckoutGuestActions,
  Coupon,
  EligibleCoupon
} from '@dcx-common/components'

const CartSummary = ({
  subtotal,
  total,
  discount,
  discountFlat,
  delivery,
  deliveryFlat,
  isCheckoutDisabled,
  familyNotice,
  translations,
  promotions,
  coupons,
  couponCode,
  handleCouponChangeInput,
  isAuthenticated,
  handleCouponAction,
  handleCartValidation,
  handleSummaryLayoutUpdate,
  handleApplyEligibleCoupon,
  hideCoupon,
  compact,
  tva,
  eligibleCoupons,
  addCouponError
}) => {
  const handleSummaryLayoutUpdateHandler = () => {
    if (handleSummaryLayoutUpdate) {
      handleSummaryLayoutUpdate()
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', handleSummaryLayoutUpdateHandler)
    window.addEventListener('resize', handleSummaryLayoutUpdateHandler)
    handleSummaryLayoutUpdateHandler()

    return () => {
      window.removeEventListener('scroll', handleSummaryLayoutUpdateHandler)
      window.removeEventListener('resize', handleSummaryLayoutUpdateHandler)
    }
  }, [])

  const renderElegibleCoupons = coupons => {
    return (
      <Container pt="4">
        {coupons.map((el, index) => (
          <EligibleCoupon
            key={index}
            coupon={el}
            translations={translations}
            handleApplyCoupon={handleApplyEligibleCoupon}
          />
        ))}
      </Container>
    )
  }

  return (
    <>
      <Container
        px="8"
        py={compact ? '4' : '8'}
        bg={
          compact
            ? Container.backgrounds.white
            : Container.backgrounds.hardWhite
        }
        divider={compact ? Container.dividers.top : null}>
        {!compact && (
          <>
            <Container fullWidth pb="8" divider={Container.dividers.bottom}>
              <TextCaption size={TextCaption.sizes.m}>
                {translations.tk_cartSummary}
              </TextCaption>
            </Container>
            <Container
              fullWidth
              pt="8"
              pb="2"
              divider={Container.dividers.bottom}>
              <Container pb="6">
                <ListContainer columns={2} xBetween="2">
                  <Container fullWidth>
                    <TextBody
                      size={TextBody.sizes.m}
                      style={TextBody.styles.grayedout}
                      {...htmlTranslation(translations, 'tk_summarySubtotal')}
                    />
                  </Container>
                  <Container fullWidth>
                    <TextBody
                      size={TextBody.sizes.m}
                      alignment={TextBody.alignments.right}
                      data-testing="Cart_TextBody_Subtotal">
                      {subtotal}
                    </TextBody>
                  </Container>
                </ListContainer>
              </Container>
              <Container pb="6">
                <ListContainer columns={2} xBetween="2">
                  <Container fullWidth>
                    <TextBody
                      size={TextBody.sizes.m}
                      style={TextBody.styles.grayedout}
                      {...htmlTranslation(translations, 'tk_summaryDelivery')}
                    />
                  </Container>
                  <Container fullWidth>
                    <TextBody
                      size={TextBody.sizes.m}
                      style={TextBody.styles.success}
                      alignment={TextBody.alignments.right}
                      data-testing="Cart_TextBody_Delivery"
                      {...(deliveryFlat === 0
                        ? htmlTranslation(translations, 'tk_freeShipping')
                        : { value: delivery })}
                    />
                  </Container>
                </ListContainer>
              </Container>
              <Container pb="6">
                <ListContainer columns={2} xBetween="2">
                  <Container fullWidth>
                    <TextBody
                      size={TextBody.sizes.m}
                      style={TextBody.styles.grayedout}
                      {...htmlTranslation(translations, 'tk_summaryDiscount')}
                    />
                  </Container>
                  <Container fullWidth>
                    <TextBody
                      size={TextBody.sizes.m}
                      style={
                        discountFlat === 0
                          ? TextBody.styles.normal
                          : TextBody.styles.error
                      }
                      alignment={TextBody.alignments.right}
                      data-testing="Cart_TextBody_Discount">
                      {discountFlat !== 0 && '- '}
                      {discount}
                    </TextBody>
                  </Container>
                </ListContainer>
              </Container>
              {promotions && promotions.length > 0 && (
                <Container pb="6" id="coupon-section">
                  <ListContainer columns={2} xBetween="2">
                    <Container fullWidth>
                      <TextBody
                        size={TextBody.sizes.m}
                        style={TextBody.styles.grayedout}
                        {...htmlTranslation(
                          translations,
                          'tk_appliedPromotions'
                        )}
                      />
                    </Container>
                    <Container fullWidth>
                      {promotions.map((promotion, index) => {
                        return (
                          <TextBody
                            key={index}
                            size={TextBody.sizes.m}
                            style={TextBody.styles.success}
                            alignment={TextBody.alignments.right}
                            data-testing="Cart_TextBody_Promotion">
                            {promotion.name}
                          </TextBody>
                        )
                      })}
                    </Container>
                  </ListContainer>
                </Container>
              )}
              {coupons && coupons.length > 0 && (
                <Container pt="2" pb="6">
                  <Container
                    px={['4', '6', '5']}
                    py={['4', '6']}
                    bg={Container.backgrounds.white}>
                    <Container fullWidth>
                      <TextCaption size={TextBody.sizes.m}>
                        {translations.tk_couponListCode}
                      </TextCaption>
                    </Container>
                    {coupons.map((coupon, index) => (
                      <Container
                        key={index}
                        pt="4"
                        display={Container.displays.flex}
                        justifyContent={Container.justifyContentValues.center}
                        alignItems={Container.alignItemsValues.center}>
                        <Container fullWidth>
                          <TextBody
                            size={TextBody.sizes.m}
                            data-testing="Cart_TextBody_Coupon">
                            {coupon.code}
                          </TextBody>
                        </Container>
                        <Container
                          pl="4"
                          display={Container.displays.flex}
                          justifyContent={Container.justifyContentValues.center}
                          alignItems={Container.alignItemsValues.center}
                          onClickHandler={() =>
                            handleCouponAction('remove', coupon.code)
                          }>
                          <Icon
                            size={Icon.sizes.xs}
                            image={Icon.images.cancel}
                          />
                        </Container>
                      </Container>
                    ))}
                  </Container>
                </Container>
              )}
            </Container>
          </>
        )}
        {!compact && !hideCoupon && tva !== 'N' && (
          <Container py="8" fullWidth divider={Container.dividers.bottom}>
            <Container>
              <Coupon
                isAuthenticated={isAuthenticated}
                translations={translations}
                couponCode={couponCode}
                handleCouponAction={() => handleCouponAction('add')}
                handleChangeInput={handleCouponChangeInput}
                addCouponError={addCouponError}
              />
            </Container>
            {eligibleCoupons &&
              eligibleCoupons.length > 0 &&
              renderElegibleCoupons(eligibleCoupons)}
          </Container>
        )}
        <Container pt={!compact ? '8' : '0'} fullWidth>
          <ListContainer columns={2} xBetween="2">
            <Container fullWidth>
              <TextTitle heading={TextTitle.headings.h5}>
                {translations.tk_summaryTotal}
              </TextTitle>
            </Container>
            <Container fullWidth>
              <TextTitle
                heading={TextTitle.headings.h5}
                alignment={TextBody.alignments.right}
                data-testing={
                  compact
                    ? 'Cart_TextTitle_TotalSticky'
                    : 'Cart_TextTitle_Total'
                }>
                {total}
              </TextTitle>
            </Container>
          </ListContainer>
        </Container>
        {isAuthenticated ? (
          <Container pt={!compact ? ['8', '8', '10'] : '4'}>
            <Button
              fullWidth
              variant={Button.variants.primary}
              text={translations.tk_summaryCheckout}
              onClickHandler={handleCartValidation}
              disabled={isCheckoutDisabled}
              data-testing={
                compact ? 'Cart_GoToCheckoutSticky' : 'Cart_GoToCheckout'
              }
            />
          </Container>
        ) : (
          <>
            <Container
              px="8"
              py="4"
              display={Container.displays.flex}
              justifyContent={Container.justifyContentValues.center}>
              {translations.tk_cartSummaryPreLoginDisclaimer && (
                <TextBody
                  size={TextBody.sizes.m}
                  alignment={TextBody.alignments.center}
                  style={TextBody.styles.error}
                  data-testing="Cart_TextBody_Disclaimer">
                  {translations.tk_cartSummaryPreLoginDisclaimer}
                </TextBody>
              )}
            </Container>
            <CartCheckoutGuestActions
              translations={translations}
              compact={compact}
            />
          </>
        )}
      </Container>
      {familyNotice && !compact && (
        <Container pt="8" px={['8', '8', '0']}>
          <Container
            fullWidth
            px={['6', '6', '8']}
            py="6"
            bg={Container.backgrounds.hardWhite}>
            <ListContainer columns={1} yBetween="3">
              <Container>
                <TextCaption size={TextCaption.sizes.m}>
                  {translations.tk_cartFamilyNoticeTitle}
                </TextCaption>
              </Container>
              <TextBody
                size={TextBody.sizes.s}
                {...htmlTranslation(
                  translations,
                  'tk_cartFamilyNoticeDescription'
                )}
              />
            </ListContainer>
          </Container>
        </Container>
      )}
    </>
  )
}

export default CartSummary
